import { Compass, Users, BarChart3, Cog, Target, Gauge } from "lucide-react";
import { SphereMark } from "@/components/brand/SphereMark";

// Stage 3 principle: "Scores are for the engine. Insights are for the user."
// No raw percentages are shown to the user; ranking, status and language carry meaning.

const DIM_ICON = {
  Direction: Compass,
  Customer: Users,
  Measurement: BarChart3,
  Operations: Cog,
  Capability: Target,
  Capacity: Gauge,
};

function SectionShell({ eyebrow, title, intro, children, id }) {
  return (
    <section id={id} className="border-t border-neutral-200 py-14 first:border-t-0 first:pt-0">
      <p className="tw-eyebrow mb-6">{eyebrow}</p>
      {title && (
        <h2 className="text-2xl font-semibold tracking-tight text-neutral-900 sm:text-3xl">{title}</h2>
      )}
      {intro && <p className="mt-4 max-w-2xl text-base leading-relaxed text-neutral-600">{intro}</p>}
      <div className={title || intro ? "mt-10" : ""}>{children}</div>
    </section>
  );
}

// --- Archetype portrait placeholder (replaceable per archetype) -----------
function ArchetypePortrait({ archetype }) {
  return (
    <div
      className="relative aspect-[3/4] w-full overflow-hidden rounded-sm border border-neutral-200 bg-white"
      data-testid="results-archetype-image"
    >
      {archetype.image ? (
        <img
          src={archetype.image}
          alt={`${archetype.name} character illustration`}
          className="h-full w-full object-contain"
          draggable={false}
        />
      ) : (
        <div className="flex h-full w-full flex-col items-center justify-center gap-6 p-8 text-center">
          <SphereMark size={22} className="tw-float" />
          <div>
            <p className="text-lg font-semibold tracking-tight text-neutral-800">{archetype.name}</p>
            <p className="tw-eyebrow mt-3 text-neutral-400">Character illustration</p>
          </div>
        </div>
      )}
    </div>
  );
}

// 1. Primary Archetype -----------------------------------------------------
export function ArchetypeSection({ primary, secondary, confidenceGap, showSecondary }) {
  return (
    <SectionShell eyebrow="Your primary archetype" id="archetype">
      <div className="grid gap-10 lg:grid-cols-12">
        <div className="mx-auto w-full max-w-xs lg:col-span-4 lg:mx-0 lg:max-w-none">
          <div className="lg:sticky lg:top-24">
            <ArchetypePortrait archetype={primary} />
          </div>
        </div>

        <div className="lg:col-span-8">
          <h2
            className="text-4xl font-bold tracking-tight text-neutral-900 sm:text-5xl"
            data-testid="results-primary-archetype-title"
          >
            {primary.name}
          </h2>
          <p className="mt-5 text-xl font-medium leading-snug text-neutral-800">{primary.hero}</p>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-neutral-600">{primary.profile}</p>

          <div className="mt-8 rounded-sm border-l-2 border-[#1408F7] bg-[var(--tw-blue-tint)] p-5">
            <p className="tw-eyebrow text-[#1408F7]">Your opportunity</p>
            <p className="mt-2 text-base font-medium text-neutral-900">{primary.opportunity}</p>
          </div>

          <p className="mt-8 text-2xl font-bold tracking-tight text-neutral-900">{primary.signature}</p>

          <div className="mt-8 grid gap-6 sm:grid-cols-2">
            <div className="rounded-sm border border-neutral-200 p-6">
              <p className="tw-eyebrow mb-4">Watch for</p>
              <ul className="space-y-2 text-sm text-neutral-700">
                {primary.watch_for.map((c) => (
                  <li key={c} className="flex gap-2">
                    <span className="mt-2 h-1 w-1 flex-shrink-0 rounded-full bg-neutral-900" />
                    {c}
                  </li>
                ))}
              </ul>
            </div>

            {showSecondary && secondary && (
              <div className="rounded-sm border border-neutral-200 p-6" data-testid="results-secondary-archetype">
                <p className="tw-eyebrow mb-2">Secondary influence</p>
                <p
                  className="text-lg font-semibold text-neutral-900"
                  data-testid="results-secondary-archetype-title"
                >
                  {secondary.name}
                </p>
                <p className="mt-2 text-sm text-neutral-600">{secondary.hero}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </SectionShell>
  );
}

// 2. Strategy / Systems / People — ranked opportunity, no percentages ------
const RANK_LABEL = ["Primary opportunity", "Secondary opportunity", "Supporting opportunity"];
const RANK_COLOR = ["#1408F7", "#000000", "#71717A"];

export function FrameworkSection({ ssp, ranked }) {
  return (
    <SectionShell
      eyebrow="The framework"
      title="Where the opportunity sits."
      intro="Your answers point to where change could make the greatest difference across the three foundations of modern marketing."
      id="framework"
    >
      <div className="space-y-4">
        {ranked.map((area, i) => {
          const score = ssp[area] ?? 0;
          // Compressed bar (45–95%) so close scores are not visually exaggerated; no numbers shown.
          const width = 45 + Math.round((score / 100) * 50);
          return (
            <div
              key={area}
              className="rounded-sm border border-neutral-200 p-6"
              data-testid={`results-score-${area.toLowerCase()}`}
            >
              <div className="flex items-center gap-5">
                <span className="text-2xl font-bold tabular-nums" style={{ color: RANK_COLOR[i] }}>
                  {String(i + 1).padStart(2, "0")}
                </span>
                <div className="flex-1">
                  <div className="flex items-baseline justify-between">
                    <span className="text-lg font-semibold text-neutral-900">{area}</span>
                    <span className="tw-eyebrow">{RANK_LABEL[i]}</span>
                  </div>
                  <div className="mt-3 h-1.5 w-full overflow-hidden rounded-sm bg-neutral-100">
                    <div
                      className="h-full rounded-sm transition-all duration-700"
                      style={{ width: `${width}%`, background: RANK_COLOR[i] }}
                    />
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </SectionShell>
  );
}

// 3. Your Three (major branded reveal) ------------------------------------
export function YourThreeSection({ items, state }) {
  if (state === "strong_foundation") {
    return (
      <SectionShell eyebrow="Your Three" id="your-three">
        <div className="rounded-sm bg-black p-10 text-white sm:p-16" data-testid="results-your-three-container">
          <p className="tw-eyebrow text-neutral-500">Your result</p>
          <h2 className="mt-4 text-4xl font-bold tracking-tight sm:text-5xl">Strong Foundation.</h2>
          <p className="mt-6 max-w-xl text-lg text-neutral-300">
            No single dimension reached the threshold for priority attention. Your marketing shows a
            strong, balanced foundation across the framework.
          </p>
        </div>
      </SectionShell>
    );
  }

  return (
    <SectionShell eyebrow="Your Three" id="your-three">
      <div className="rounded-sm bg-black p-10 text-white sm:p-16" data-testid="results-your-three-container">
        <h2 className="text-2xl font-semibold tracking-tight text-white">Your Three</h2>
        <p className="mt-4 max-w-xl text-lg text-neutral-400">
          Based on your answers, these are the things that matter most right now.
        </p>
        <div className="mt-10 space-y-3">
          {items.map((item, i) => (
            <div key={item.dimension} className="flex items-baseline gap-5">
              <span className="text-lg font-medium tabular-nums text-[#1408F7]">
                {String(i + 1).padStart(2, "0")}
              </span>
              <span className="text-4xl font-bold uppercase tracking-tight sm:text-6xl">
                {item.dimension}.
              </span>
            </div>
          ))}
        </div>
      </div>
    </SectionShell>
  );
}

// 4. Six Dimensions — ranked, status + descriptor, no percentages ---------
const STATUS_STYLE = {
  Priority: "bg-[#1408F7] text-white border-transparent",
  Opportunity: "bg-neutral-900 text-white border-transparent",
  Developing: "bg-white text-neutral-600 border-neutral-300",
  "Strong foundation": "bg-white text-neutral-400 border-neutral-200",
};

export function DimensionsSection({ status, ranked, descriptors }) {
  return (
    <SectionShell
      eyebrow="Six dimensions"
      title="The detailed picture"
      intro="The framework broken down. This supports your result — the priorities above are what to act on first."
      id="dimensions"
    >
      <div className="divide-y divide-neutral-200 overflow-hidden rounded-sm border border-neutral-200">
        {ranked.map((dim) => {
          const Icon = DIM_ICON[dim];
          const label = status?.[dim] || "Not assessed";
          const style = STATUS_STYLE[label] || "bg-white text-neutral-300 border-neutral-200";
          return (
            <div
              key={dim}
              className="flex items-center gap-4 p-5 sm:gap-6"
              data-testid={`results-dimension-${dim.toLowerCase()}`}
            >
              <Icon className="h-5 w-5 flex-shrink-0 text-neutral-400" strokeWidth={1.5} />
              <div className="min-w-0 flex-1">
                <h3 className="text-base font-semibold text-neutral-900">{dim}</h3>
                <p className="mt-0.5 truncate text-sm text-neutral-500">{descriptors?.[dim]}</p>
              </div>
              <span
                className={`flex-shrink-0 rounded-sm border px-3 py-1 text-[11px] font-semibold uppercase tracking-wider ${style}`}
              >
                {label}
              </span>
            </div>
          );
        })}
      </div>
    </SectionShell>
  );
}

// 5. What This Means — deterministic interpretation (assembled from approved copy)
export function InterpretationSection({ interpretation }) {
  const paras = interpretation?.paragraphs;
  return (
    <SectionShell eyebrow="Interpretation" title="What this means" id="interpretation">
      {paras && paras.length ? (
        <div className="max-w-3xl space-y-5" data-testid="results-interpretation">
          {paras.map((p, i) =>
            p.emphasis ? (
              <p
                key={i}
                className="border-l-2 border-[#1408F7] pl-5 text-lg font-medium leading-relaxed text-neutral-900"
              >
                {p.text}
              </p>
            ) : (
              <p key={i} className="text-lg leading-relaxed text-neutral-700">
                {p.text}
              </p>
            )
          )}
        </div>
      ) : (
        <div className="h-px w-full bg-transparent" aria-hidden="true" />
      )}
    </SectionShell>
  );
}

// 6. Talk It Through / CTA -------------------------------------------------
const BOOKING_URL = process.env.REACT_APP_BOOKING_URL || null; // set later, no code change needed

export function TalkItThroughSection() {
  return (
    <SectionShell eyebrow="Talk it through" id="talk-it-through">
      <div className="rounded-sm bg-black p-8 text-white sm:p-12">
        <p className="tw-eyebrow text-neutral-500">Talk it through</p>
        <h2 className="mt-4 text-3xl font-bold tracking-tight sm:text-4xl">
          Your results are a starting point.
        </h2>
        <p className="mt-5 max-w-2xl text-base leading-relaxed text-neutral-300">
          A diagnostic can show where the signals are. The next step is understanding what&apos;s causing
          them. Book a conversation with Three Words Full to talk through your results and explore what they
          could mean for your organisation.
        </p>
        <a
          href={BOOKING_URL || "#"}
          target="_blank"
          rel="noopener noreferrer"
          className="tw-btn-primary tw-focus mt-8 inline-flex rounded-sm px-8 py-4 text-sm font-semibold uppercase tracking-wide"
          data-testid="results-book-conversation-button"
        >
          Book a results conversation
        </a>
      </div>
    </SectionShell>
  );
}
